/**
 * Modal that lets the user pick BRRRR / Flip / Rental when adding a saved
 * property to the pipeline.
 *
 * Used by:
 *   - NewDealDrawer (Pipeline page) → after picking a saved property
 *   - PropertyDetailModal "Add to Pipeline" CTA (Phase 2 wiring)
 *
 * Controlled component: parent owns `open`. On confirm, calls `onSelect`
 * with the chosen strategy and closes via `onOpenChange(false)`.
 */
import { ArrowLeftRight, Home, RefreshCcw } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Strategy } from "@/types/pipeline";

interface StrategyPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user picks a strategy. Parent closes the dialog. */
  onSelect: (strategy: Strategy) => void;
  /** Optional title override (e.g. "Add 915 Shelton Street to Pipeline"). */
  title?: string;
  /** Disable Rental option when the property isn't rentable. */
  disabledStrategies?: Strategy[];
  /** Pending state — disable buttons while the mutation is in flight. */
  isPending?: boolean;
}

interface StrategyOption {
  value: Strategy;
  label: string;
  description: string;
  Icon: typeof Home;
}

const OPTIONS: StrategyOption[] = [
  {
    value: "BRRRR",
    label: "BRRRR",
    description: "Buy, Rehab, Rent, Refinance, Repeat",
    Icon: RefreshCcw,
  },
  {
    value: "Flip",
    label: "Flip",
    description: "Buy low, renovate, sell for profit",
    Icon: ArrowLeftRight,
  },
  {
    value: "Rental",
    label: "Rental",
    description: "Hold long-term and rent it out",
    Icon: Home,
  },
];

export function StrategyPickerDialog({
  open,
  onOpenChange,
  onSelect,
  title,
  disabledStrategies,
  isPending,
}: StrategyPickerDialogProps) {
  const disabled = new Set<Strategy>(disabledStrategies ?? []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title ?? "Add to Pipeline"}</DialogTitle>
          <DialogDescription>
            Select a strategy for this property
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {OPTIONS.map((opt) => {
            const isDisabled = disabled.has(opt.value) || isPending;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={isDisabled}
                onClick={() => onSelect(opt.value)}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-lg border-2 border-border bg-muted/40 p-4 text-center transition",
                  "hover:border-primary hover:bg-primary/5",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-muted/40",
                )}
                aria-label={`Add as ${opt.label}: ${opt.description}`}
              >
                <opt.Icon
                  className="h-7 w-7 text-primary"
                  aria-hidden
                />
                <span className="text-sm font-bold text-foreground">
                  {opt.label}
                </span>
                <span className="text-xs leading-tight text-muted-foreground">
                  {opt.description}
                </span>
              </button>
            );
          })}
        </div>

        <DialogFooter className="sm:justify-start">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="w-full"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
